import React, { useReducer } from 'react';
import { isTenantCustomCatalogProvider } from './LessonsPage/utils/topicUtils';
import { getTopicInstructionalRollup } from './LessonsPage/utils/topicInstructionalUnits';
import { UnitStateKey, LessonStateKey } from './InProgressUnitsPanel/lessonStepState';
import { InlineHoursEditor } from './TopicCard/InlineHoursEditor';
import { InlineTextFieldEditor } from './TopicCard/InlineTextFieldEditor';
import { InlineListEditor } from './TopicCard/InlineListEditor';

interface TopicCardProps {
  topic: any;
  index: number;
  isExpanded: boolean;
  onToggleExpanded: () => void;
  onRemove: () => void;
  buildBreadcrumb: (topic: any) => string;
  getProviderName?: (providerKey: string | null | undefined) => string;
  units?: any[]; // Units data to get assessment scores
  passFailThreshold?: number | null; // Student's pass/fail threshold
  onLoadTopicDetails?: (topicId: string, providerKey?: string) => void;
  loadingDetails?: boolean;
  curriculumProviderKey?: string;
  studentId?: string | null;
}

export function TopicCard({ 
  topic, 
  index, 
  isExpanded, 
  onToggleExpanded, 
  onRemove, 
  buildBreadcrumb,
  getProviderName,
  units = [],
  passFailThreshold = null,
  onLoadTopicDetails,
  loadingDetails = false,
  curriculumProviderKey,
  studentId = null
}: TopicCardProps) {
  /** Bump after optimistic inline edits mutate `topic`; props reference is unchanged so React would not re-render otherwise. */
  const [, bumpTopicView] = useReducer((x: number) => x + 1, 0);

  const patchTopicTextField = (fieldBase: string, newValue: string | null) => {
    if (topic.e180_TopicId) {
      (topic as Record<string, unknown>)['e180_' + fieldBase] =
        newValue === null ? null : newValue;
    } else if (topic.e179_TopicId) {
      (topic as Record<string, unknown>)['e179_' + fieldBase] =
        newValue === null ? null : newValue;
    }
    bumpTopicView();
  };

  const patchTopicListField = (fieldBase: string, newValue: string[] | null) => {
    const arr = newValue && newValue.length > 0 ? newValue : [];
    if (topic.e180_TopicId) {
      (topic as Record<string, unknown>)['e180_' + fieldBase] = arr;
    } else if (topic.e179_TopicId) {
      (topic as Record<string, unknown>)['e179_' + fieldBase] = arr;
    }
    bumpTopicView();
  };

  const topicId = topic.e179_TopicId || topic.e180_TopicId || `topic-${index}`;
  // Check both e179 and e180 structures for provider key
  const providerKey = topic.e179_ProviderKey || topic.e180_ProviderKey;
  const providerName = getProviderName && providerKey ? getProviderName(providerKey) : null;
  
  // For student curriculum topics, all fields should already be in e180_* structure
  // Only load details for provider topics (e179) that don't have details loaded yet
  const isStudentCurriculumTopic = !!topic.e180_TopicId;
  /** Library leaf from E308→E179 mapping: edit tenant catalog. Student curriculum tree (`e180`) is read-only here — edit custom topics only from the tenant custom catalog tree. */
  const isTenantCustomCatalogLibraryTopic =
    isTenantCustomCatalogProvider(providerKey) && !isStudentCurriculumTopic;
  const topicInlineEditTarget = isTenantCustomCatalogLibraryTopic
    ? 'tenant'
    : isStudentCurriculumTopic
      ? 'none'
      : 'student';
  // Tenant catalog / hand-authored student topic (no ProviderTopicId)
  const isCustomTopic =
    isTenantCustomCatalogProvider(providerKey) ||
    (isStudentCurriculumTopic && !topic.e180_ProviderTopicId);
  
  // Helper to get field value from either e179 or e180 structure
  const getField = (e179Field: any, e180Field: any) => e179Field ?? e180Field;
  const getName = () => getField(topic.e179_Name, topic.e180_Name);
  const getMinHours = () => {
    const hours = getField(topic.e179_MinHours, topic.e180_MinHours);
    // Return null if hours is 0 or null/undefined to hide it
    return hours && hours > 0 ? hours : null;
  };
  const getDetails = () => getField(topic.e179_Details, topic.e180_Details);
  const getDetailsLoaded = () => getField(topic.e179_DetailsLoaded, topic.e180_DetailsLoaded);
  const getOutcome = () => getField(topic.e179_Outcome, topic.e180_Outcome);
  const getMisconceptions = () => getField(topic.e179_Misconceptions, topic.e180_Misconceptions);
  const getTeacherTip = () => getField(topic.e179_TeacherTip, topic.e180_TeacherTip);
  const getPriorKnowledgeRequired = () => getField(topic.e179_PriorKnowledgeRequired, topic.e180_PriorKnowledgeRequired);
  const getNationalCurriculumContent = () => getField(topic.e179_NationalCurriculumContent, topic.e180_NationalCurriculumContent);
  const getKeywords = () => getField(topic.e179_Keywords, topic.e180_Keywords);
  const getLearningObjectives = () => getField(topic.e179_LearningObjectives, topic.e180_LearningObjectives);
  const getSupervisionLevel = () => getField(topic.e179_SupervisionLevel, topic.e180_SupervisionLevel);
  const getChildTopics = () => getField(topic.e179_ChildTopics, topic.e180_ChildTopics);

  // Helper function to get assessment score for this topic
  const getAssessmentScoreForTopic = (topicId: string): number | null => {
    if (!units || units.length === 0 || !topicId) return null;
    
    // Normalize topic ID for comparison (handle GUID format)
    const normalizeId = (id: string) => id?.toLowerCase().trim();
    const normalizedTopicId = normalizeId(topicId);
    
    // Find completed units that contain this topic and have an assessment
    const matchingUnits = units.filter(unit => {
      const isCompleted = unit.e251_State === UnitStateKey.Complete;
      const hasAssessment = unit.e251_AssessmentId && unit.e251_AssessmentStatus?.e251_Result?.e251_Score != null;
      const containsTopic = unit.e251_TopicIds?.some((id: string) => {
        if (!id) return false;
        return normalizeId(id) === normalizedTopicId;
      });
      return isCompleted && hasAssessment && containsTopic;
    });
    
    if (matchingUnits.length === 0) return null;
    
    // Return the most recent assessment score (units are ordered newest first)
    const mostRecentUnit = matchingUnits[0];
    return mostRecentUnit.e251_AssessmentStatus?.e251_Result?.e251_Score ?? null;
  };

  // Helper functions to check topic state
  const isTopicCompleted = (topic: any): boolean => {
    const state = topic.e180_State || topic.e179_State;
    if (!state) return false;
    // Check both selectedKeys (from EnumerationFlags serialization) and Values
    const keys = state.selectedKeys || state.Values || [];
    return keys.includes(LessonStateKey.Completed);
  };

  const isTopicInProgress = (topic: any): boolean => {
    const state = topic.e180_State || topic.e179_State;
    if (!state) return false;
    // Check both selectedKeys (from EnumerationFlags serialization) and Values
    const keys = state.selectedKeys || state.Values || [];
    return keys.includes(LessonStateKey.InProgress);
  };

  const isTopicReady = (topic: any): boolean => {
    const state = topic.e180_State || topic.e179_State;
    if (!state) return true; // Default to ready if no state
    // Check both selectedKeys (from EnumerationFlags serialization) and Values
    const keys = state.selectedKeys || state.Values || [];
    const hasReady = keys.includes(LessonStateKey.Ready);
    const hasInProgress = keys.includes(LessonStateKey.InProgress);
    const hasCompleted = keys.includes(LessonStateKey.Completed);
    return hasReady && !hasInProgress && !hasCompleted;
  };

  const canRemoveTopicFromEnumeration = (topic: any): boolean => {
    // Topics can be removed if they are NOT completed and NOT in progress
    // Only restrict removal if topic is Completed or InProgress
    return !isTopicCompleted(topic) && !isTopicInProgress(topic);
  };

  // Parse Details text to extract structured sections
  const parseDetailsText = (detailsText: string) => {
    if (!detailsText) return null;
    
    const sections: { [key: string]: string } = {};
    const text = detailsText.trim();
    
    // Section labels to look for (case-insensitive, with colon)
    const sectionLabels = [
      { label: 'Outcome:', key: 'outcome' },
      { label: 'Misconception:', key: 'misconception' },
      { label: 'Misconceptions:', key: 'misconception' },
      { label: 'Response:', key: 'response' },
      { label: 'Teacher tip:', key: 'teacherTip' },
    ];
    
    // Find all section positions
    const sectionPositions: Array<{ label: string; key: string; index: number }> = [];
    for (const { label, key } of sectionLabels) {
      // Escape special regex characters and match case-insensitively
      const escapedLabel = label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(escapedLabel, 'i');
      const match = text.match(regex);
      if (match && match.index !== undefined) {
        sectionPositions.push({ label, key, index: match.index });
      }
    }
    
    // Sort by position
    sectionPositions.sort((a, b) => a.index - b.index);
    
    // If no sections found, return null
    if (sectionPositions.length === 0) {
      return null;
    }
    
    // Extract each section
    for (let i = 0; i < sectionPositions.length; i++) {
      const current = sectionPositions[i];
      const next = sectionPositions[i + 1];
      
      // Find the actual label length (case-insensitive match)
      const labelMatch = text.substring(current.index).match(new RegExp(current.label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'i'));
      const labelLength = labelMatch ? labelMatch[0].length : current.label.length;
      
      const startIndex = current.index + labelLength;
      const endIndex = next ? next.index : text.length;
      
      const content = text.substring(startIndex, endIndex).trim();
      if (content) {
        sections[current.key] = content;
      }
    }
    
    return { sections, remainingText: null };
  };

  const instructionalRollupForTopic =
    units.length > 0 && topicId !== `topic-${index}`
      ? getTopicInstructionalRollup(units, topic.e179_TopicId || topic.e180_TopicId || '')
      : null;

  const topicCompletedVisual =
    instructionalRollupForTopic && instructionalRollupForTopic.qualifyingUnits.length > 0
      ? instructionalRollupForTopic.rollup === 'allComplete'
      : isTopicCompleted(topic);

  const showRemoveButton =
    instructionalRollupForTopic && instructionalRollupForTopic.qualifyingUnits.length > 0
      ? instructionalRollupForTopic.rollup === 'none' &&
        !isTopicCompleted(topic) &&
        !isTopicInProgress(topic)
      : canRemoveTopicFromEnumeration(topic);

  const topicCompleted = topicCompletedVisual;
  const assessmentScore = topicCompleted ? getAssessmentScoreForTopic(topicId) : null;

  // Handle expand/collapse with lazy loading
  const handleToggleExpanded = () => {
    const wasExpanded = isExpanded;
    const detailsLoaded = getDetailsLoaded();
    console.log('TopicCard: Toggling expanded state', { wasExpanded, topicId, hasOnLoadTopicDetails: !!onLoadTopicDetails, detailsLoaded });
    onToggleExpanded();
    
    // If expanding and details not loaded, trigger lazy load
    // Only built-in curricula (e.g. oak) have JSON served by C286; tenant catalog is fully in E308/E180 payloads.
    if (
      !wasExpanded &&
      !detailsLoaded &&
      onLoadTopicDetails &&
      !isStudentCurriculumTopic &&
      providerKey &&
      !isTenantCustomCatalogProvider(providerKey)
    ) {
      const providerKeyToUse = providerKey || curriculumProviderKey;
      console.log('TopicCard: Attempting to load details', {
        topicId,
        providerKeyToUse,
        curriculumProviderKey,
        providerKey,
        detailsLoaded,
        isStudentCurriculumTopic
      });
      if (providerKeyToUse) {
        onLoadTopicDetails(topicId, providerKeyToUse);
      } else {
        console.warn('TopicCard: No provider key available to load details');
      }
    }
  };

  return (
    <div className={`group bg-gradient-to-r from-white to-action-blue/10 rounded-xl border shadow-md hover:shadow-lg transition-all duration-200 ${
      topicCompleted ? 'border-confirm-action/40 bg-confirm-action/10' : 'border-action-blue/30'
    }`}>
      <div className="p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center flex-1">
            <button
              onClick={handleToggleExpanded}
              className="flex items-center text-left flex-1 hover:bg-action-blue/10 rounded-lg p-2 -m-2 transition-colors"
            >
              <svg
                className={`w-5 h-5 mr-3 text-action-blue transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-xl font-bold text-gray-900">
                    {getName() || 'Untitled Topic'}
                  </h3>
                  {topicCompleted && (
                    <>
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-confirm-action/15 text-confirm-action-dark">
                        ✓ Completed
                      </span>
                      {assessmentScore != null && (
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                          assessmentScore < (passFailThreshold ?? 50)
                            ? 'bg-orange-100 text-orange-700'
                            : 'bg-confirm-action/15 text-confirm-action-dark'
                        }`}>
                          Score: {assessmentScore}%
                        </span>
                      )}
                    </>
                  )}
                </div>
                {!isExpanded && (
                  <div className="mt-1">
                    <p className="text-sm text-gray-600 mb-3">
                      {buildBreadcrumb(topic)}
                    </p>
                    {providerName && (
                      <p className="text-sm text-gray-500 mb-2">
                        Provider: <span className="text-action-blue font-medium">{providerName}</span>
                      </p>
                    )}
                    <div className="flex flex-nowrap items-center gap-2 text-sm text-gray-500">
                      <span className="shrink-0 whitespace-nowrap">Minimum Duration:</span>
                      <span className="inline-flex shrink-0 items-center">
                        <InlineHoursEditor
                          minHours={getMinHours()}
                          topicId={topicId}
                          studentId={studentId}
                          editTarget={topicInlineEditTarget}
                          onUpdate={(newHours) => {
                            if (topic.e180_TopicId) {
                              topic.e180_MinHours = newHours ?? 0;
                            } else if (topic.e179_TopicId) {
                              topic.e179_MinHours = newHours ?? 0;
                            }
                            bumpTopicView();
                          }}
                        />
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </button>
          </div>
          {showRemoveButton && (
            <button
              onClick={onRemove}
              className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 p-2 rounded-full hover:bg-destructive-action/15 text-gray-400 hover:text-destructive-action"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
        
        {isExpanded && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="mb-2">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-3">
                    {buildBreadcrumb(topic)}
                  </p>
                  {providerName && (
                    <p className="text-sm text-gray-500 mb-2">
                      Provider: <span className="text-action-blue font-medium">{providerName}</span>
                    </p>
                  )}
                  <div className="flex flex-nowrap items-center gap-2 text-sm text-gray-500">
                    <span className="shrink-0 whitespace-nowrap">Minimum Duration:</span>
                    <span className="inline-flex shrink-0 items-center">
                      <InlineHoursEditor
                        minHours={getMinHours()}
                        topicId={topicId}
                        studentId={studentId}
                        editTarget={topicInlineEditTarget}
                        onUpdate={(newHours) => {
                          if (topic.e180_TopicId) {
                            topic.e180_MinHours = newHours ?? 0;
                          } else if (topic.e179_TopicId) {
                            topic.e179_MinHours = newHours ?? 0;
                          }
                          bumpTopicView();
                        }}
                      />
                    </span>
                  </div>
                </div>
              </div>
            </div>
            
            {loadingDetails && (
              <div className="mb-4 flex items-center justify-center py-4">
                <svg className="animate-spin h-5 w-5 text-action-blue mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span className="text-sm text-gray-500">Loading topic details...</span>
              </div>
            )}
            
            {!loadingDetails && (
              <>
                {(() => {
                  // Parse Details text if it exists
                  const details = getDetails();
                  const parsedDetails = details ? parseDetailsText(details) : null;
                  const outcome = getOutcome();
                  const misconceptions = getMisconceptions();
                  const teacherTip = getTeacherTip();
                  
                  return (
                    <>
                      {details && !parsedDetails && (
                        <div className="mb-4 pt-4 border-t border-gray-200">
                          <h4 className="text-sm font-semibold text-gray-800 mb-2">Details</h4>
                          {isCustomTopic ? (
                            <InlineTextFieldEditor
                              value={details}
                              topicId={topicId}
                              studentId={studentId}
                              editTarget={topicInlineEditTarget}
                              fieldName="Details"
                              label="Details"
                              rows={5}
                              onUpdate={(v) => patchTopicTextField('Details', v)}
                            />
                          ) : (
                            <p className="text-gray-700 leading-relaxed">
                              {details}
                            </p>
                          )}
                        </div>
                      )}

                      {parsedDetails?.remainingText && (
                        <div className="mb-4 pt-4 border-t border-gray-200">
                          <h4 className="text-sm font-semibold text-gray-800 mb-2">Details</h4>
                          <p className="text-gray-700 leading-relaxed">
                            {parsedDetails.remainingText}
                          </p>
                        </div>
                      )}

                      {(parsedDetails?.sections.outcome || outcome) && (
                        <div className="mb-4 pt-4 border-t border-gray-200">
                          <h4 className="text-sm font-semibold text-gray-800 mb-2">Outcome</h4>
                          {isCustomTopic ? (
                            <InlineTextFieldEditor
                              value={outcome}
                              topicId={topicId}
                              studentId={studentId}
                              editTarget={topicInlineEditTarget}
                              fieldName="Outcome"
                              label="Outcome"
                              onUpdate={(v) => patchTopicTextField('Outcome', v)}
                            />
                          ) : (
                            <p className="text-gray-700 leading-relaxed">
                              {parsedDetails?.sections.outcome || outcome}
                            </p>
                          )}
                        </div>
                      )}

                      {(parsedDetails?.sections.misconception || misconceptions) && (
                        <div className="mb-4 pt-4 border-t border-gray-200">
                          <h4 className="text-sm font-semibold text-gray-800 mb-2">Misconceptions</h4>
                          {isCustomTopic ? (
                            <InlineTextFieldEditor
                              value={misconceptions}
                              topicId={topicId}
                              studentId={studentId}
                              editTarget={topicInlineEditTarget}
                              fieldName="Misconceptions"
                              label="Misconceptions"
                              onUpdate={(v) => patchTopicTextField('Misconceptions', v)}
                            />
                          ) : (
                            <p className="text-gray-700 leading-relaxed">
                              {parsedDetails?.sections.misconception || misconceptions}
                              {parsedDetails?.sections.response && (
                                <> {parsedDetails.sections.response}</>
                              )}
                            </p>
                          )}
                        </div>
                      )}

                      {parsedDetails?.sections.response && !parsedDetails?.sections.misconception && !misconceptions && (
                        <div className="mb-4 pt-4 border-t border-gray-200">
                          <h4 className="text-sm font-semibold text-gray-800 mb-2">Response</h4>
                          <p className="text-gray-700 leading-relaxed">
                            {parsedDetails.sections.response}
                          </p>
                        </div>
                      )}

                      {(parsedDetails?.sections.teacherTip || teacherTip) && (
                        <div className="mb-4 pt-4 border-t border-gray-200">
                          <h4 className="text-sm font-semibold text-gray-800 mb-2">Teacher Tip</h4>
                          {isCustomTopic ? (
                            <InlineTextFieldEditor
                              value={teacherTip}
                              topicId={topicId}
                              studentId={studentId}
                              editTarget={topicInlineEditTarget}
                              fieldName="TeacherTip"
                              label="Teacher Tip"
                              onUpdate={(v) => patchTopicTextField('TeacherTip', v)}
                            />
                          ) : (
                            <p className="text-gray-700 leading-relaxed">
                              {parsedDetails?.sections.teacherTip || teacherTip}
                            </p>
                          )}
                        </div>
                      )}

                      {isCustomTopic && (
                        <>
                          {!details && !parsedDetails && (
                            <div className="mb-4 pt-4 border-t border-gray-200">
                              <h4 className="text-sm font-semibold text-gray-800 mb-2">Details</h4>
                              <InlineTextFieldEditor
                                value={null}
                                topicId={topicId}
                                studentId={studentId}
                                editTarget={topicInlineEditTarget}
                                fieldName="Details"
                                label="Details"
                                rows={5}
                                onUpdate={(v) => patchTopicTextField('Details', v)}
                              />
                            </div>
                          )}
                          {!outcome && !parsedDetails?.sections.outcome && (
                            <div className="mb-4 pt-4 border-t border-gray-200">
                              <h4 className="text-sm font-semibold text-gray-800 mb-2">Outcome</h4>
                              <InlineTextFieldEditor
                                value={null}
                                topicId={topicId}
                                studentId={studentId}
                                editTarget={topicInlineEditTarget}
                                fieldName="Outcome"
                                label="Outcome"
                                onUpdate={(v) => patchTopicTextField('Outcome', v)}
                              />
                            </div>
                          )}
                          {!misconceptions && !parsedDetails?.sections.misconception && (
                            <div className="mb-4 pt-4 border-t border-gray-200">
                              <h4 className="text-sm font-semibold text-gray-800 mb-2">Misconceptions</h4>
                              <InlineTextFieldEditor
                                value={null}
                                topicId={topicId}
                                studentId={studentId}
                                editTarget={topicInlineEditTarget}
                                fieldName="Misconceptions"
                                label="Misconceptions"
                                onUpdate={(v) => patchTopicTextField('Misconceptions', v)}
                              />
                            </div>
                          )}
                          {!teacherTip && !parsedDetails?.sections.teacherTip && (
                            <div className="mb-4 pt-4 border-t border-gray-200">
                              <h4 className="text-sm font-semibold text-gray-800 mb-2">Teacher Tip</h4>
                              <InlineTextFieldEditor
                                value={null}
                                topicId={topicId}
                                studentId={studentId}
                                editTarget={topicInlineEditTarget}
                                fieldName="TeacherTip"
                                label="Teacher Tip"
                                onUpdate={(v) => patchTopicTextField('TeacherTip', v)}
                              />
                            </div>
                          )}
                        </>
                      )}
                    </>
                  );
                })()}
                
                {(() => {
                  const priorKnowledge = getPriorKnowledgeRequired();
                  return (priorKnowledge && priorKnowledge.length > 0) || isCustomTopic ? (
                    <div className="mb-4 pt-4 border-t border-gray-200">
                      <h4 className="text-sm font-semibold text-gray-800 mb-2">Prior Knowledge Required</h4>
                      {isCustomTopic ? (
                        <InlineListEditor
                          value={priorKnowledge}
                          topicId={topicId}
                          studentId={studentId}
                          editTarget={topicInlineEditTarget}
                          fieldName="PriorKnowledgeRequired"
                          label="Prior Knowledge Required"
                          onUpdate={(v) =>
                            patchTopicListField('PriorKnowledgeRequired', v)
                          }
                        />
                      ) : (
                        <ul className="list-disc list-inside space-y-1">
                          {priorKnowledge.map((item: string, idx: number) => (
                            <li key={idx} className="text-gray-700 leading-relaxed">{item}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ) : null;
                })()}
                
                {(() => {
                  const nationalCurriculum = getNationalCurriculumContent();
                  return nationalCurriculum && nationalCurriculum.length > 0 && (
                    <div className="mb-4 pt-4 border-t border-gray-200">
                      <h4 className="text-sm font-semibold text-gray-800 mb-2">National Curriculum Content</h4>
                      <ul className="list-disc list-inside space-y-1">
                        {nationalCurriculum.map((item: string, idx: number) => (
                          <li key={idx} className="text-gray-700 leading-relaxed">{item}</li>
                        ))}
                      </ul>
                    </div>
                  );
                })()}
                
                {(() => {
                  const keywords = getKeywords();
                  return keywords && keywords.length > 0 && (
                    <div className="mb-4 pt-4 border-t border-gray-200">
                      <h4 className="text-sm font-semibold text-gray-800 mb-2">Keywords</h4>
                      <div className="flex flex-wrap gap-2">
                        {keywords.map((keyword: any, idx: number) => {
                          const keywordText = typeof keyword === 'string' ? keyword : (keyword.e179_Keyword || keyword.e180_Keyword);
                          const keywordDesc = typeof keyword === 'object' ? (keyword.e179_Description || keyword.e180_Description) : null;
                          return (
                            <div key={idx} className="bg-action-blue/10 border border-action-blue/30 rounded-md px-3 py-1.5">
                              <span className="text-sm font-medium text-action-blue-dark">{keywordText}</span>
                              {keywordDesc && (
                                <span className="text-xs text-action-blue-dark ml-2">— {keywordDesc}</span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}
                
                {(() => {
                  const learningObjectives = getLearningObjectives();
                  return (learningObjectives && learningObjectives.length > 0) || isCustomTopic ? (
                    <div className="mb-4 pt-4 border-t border-gray-200">
                      <h4 className="text-sm font-semibold text-gray-800 mb-2">Learning Objectives</h4>
                      {isCustomTopic ? (
                        <InlineListEditor
                          value={learningObjectives}
                          topicId={topicId}
                          studentId={studentId}
                          editTarget={topicInlineEditTarget}
                          fieldName="LearningObjectives"
                          label="Learning Objectives"
                          onUpdate={(v) => patchTopicListField('LearningObjectives', v)}
                        />
                      ) : (
                        <ul className="list-disc list-inside space-y-1">
                          {learningObjectives.map((item: string, idx: number) => (
                            <li key={idx} className="text-gray-700 leading-relaxed">{item}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ) : null;
                })()}
                
                {(() => {
                  const supervisionLevel = getSupervisionLevel();
                  return (supervisionLevel || isCustomTopic) ? (
                    <div className="mb-4 pt-4 border-t border-gray-200">
                      <h4 className="text-sm font-semibold text-gray-800 mb-2">Supervision Level</h4>
                      {isCustomTopic ? (
                        <InlineTextFieldEditor
                          value={supervisionLevel}
                          topicId={topicId}
                          studentId={studentId}
                          editTarget={topicInlineEditTarget}
                          fieldName="SupervisionLevel"
                          label="Supervision Level"
                          rows={1}
                          onUpdate={(v) => patchTopicTextField('SupervisionLevel', v)}
                        />
                      ) : (
                        <p className="text-gray-700 leading-relaxed">{supervisionLevel}</p>
                      )}
                    </div>
                  ) : null;
                })()}
                
                {(() => {
                  const childTopics = getChildTopics();
                  return childTopics && childTopics.length > 0 && (
                    <div className="mb-4 pt-4 border-t border-gray-200">
                      <h4 className="text-sm font-semibold text-gray-800 mb-2">Subtopics ({childTopics.length})</h4>
                      <div className="space-y-2">
                        {childTopics.map((subtopic: any, subIndex: number) => (
                          <div key={subIndex} className="bg-gray-50 rounded-md p-3">
                            <div className="font-medium text-gray-900">
                              {subtopic.e179_Name || subtopic.e180_Name || `Subtopic ${subIndex + 1}`}
                            </div>
                            {(subtopic.e179_Details || subtopic.e180_Details) && (
                              <div className="text-sm text-gray-600 mt-1">
                                {subtopic.e179_Details || subtopic.e180_Details}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
